import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../supabase.js';
import { useAuth } from '../AuthContext.jsx';
import { clientPath } from '../lib/clientLink.js';
import OnboardingChecklist, { checklistComplete } from '../components/OnboardingChecklist.jsx';
import { ResultsHomeTile } from './Results.jsx';
import { DeadlineNote, AutoBadge, fmtDay } from '../components/ApprovalBits.jsx';
import { AlertRows, AlertGroups, markAlertsRead } from '../components/AlertBits.jsx';
import { FailedReplace } from '../components/ReplaceBits.jsx';
import SectionHeading from '../components/HowItWorksPanel.jsx';

function formatDate(iso) {
  if (!iso) return '';
  const d = typeof iso === 'string' && iso.length === 10 ? new Date(iso + 'T12:00:00Z') : new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function kindTag(kind) {
  return kind === 'quarterly_plan' ? 'Plan'
       : kind === 'post_questions' ? 'Questions'
       : kind === 'post_draft'     ? 'Draft'
       : kind === 'baseline_analysis' ? 'Analysis'
       : kind === 'analytics_alert' ? 'Alert'
       : kind === 'gmb_audit' ? 'GMB'
       : kind === 'seo_task_list' ? 'SEO'   // Session 22
       : kind === 'seo_fix' ? 'SEO fix'     // Session 24
       : kind === 'plan_replace' ? 'Replace'
       : kind === 'publish' ? 'Publish'   // Session 12B: a failed publish (any adapter)
       : kind;
}

// Session 8 (B1): the agency inbox is split in two. "Needs your action" holds
// actionable rows (agency_review plans, baselines, drafts) across every
// client. "Waiting on client" is a read-only list of items already sent to a
// client (actionable = false), shown underneath with no buttons and hidden
// when empty. Only the action list counts toward badges. The client view is
// unchanged: one list, filtered defensively to audience = 'client'.
//
// Session 14: the agency inbox follows the client switcher for every row
// type (plans, drafts, baselines, GMB, waiting rows and traffic alerts). An
// "All clients" toggle shows every client, with alerts grouped per client.
// Alerts sit in their own section with per-user read state; read alerts fold
// into one "N earlier alerts (read)" line and never count toward badges.
export default function Attention() {
  const { activeId, activeClient, isAgency, clients, inboxAll, setInboxAll, bumpInbox } = useAuth();
  const [markingAll, setMarkingAll] = useState(false);
  const [markErr, setMarkErr] = useState(null);
  const [rows,  setRows]  = useState(null);
  const [error, setError] = useState(null);
  const [onb,   setOnb]   = useState(null);   // Session 6: agency progress card
  const [failedReps, setFailedReps] = useState({});   // Session 19: v_portal_replacements rows for failed Replace items
  const [doneNote, setDoneNote] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setOnb(null);
      if (!activeId || !isAgency) return;
      const { data } = await supabase.from('v_portal_onboarding').select('*').eq('client_id', activeId).maybeSingle();
      if (!cancelled) setOnb(data || null);
    })();
    return () => { cancelled = true; };
  }, [activeId, isAgency]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setRows(null); setError(null);
      if (!activeId) return;
      let q = supabase
        .from('v_portal_attention')
        .select('*')
        .order('sort_at', { ascending: true, nullsFirst: false });
      // Agency: the switcher's client, or every client in "All clients" mode
      // (the view already scopes to the agency role). Client: own rows only.
      q = isAgency
        ? (inboxAll ? q.eq('audience', 'agency') : q.eq('audience', 'agency').eq('client_id', activeId))
        : q.eq('client_id', activeId).eq('audience', 'client');
      const { data, error } = await q;
      if (cancelled) return;
      if (error) { setError(error.message); return; }
      setRows(data || []);
      // Session 19: a failed Replace gets Dismiss / Try again; they need the
      // replacement's plan, slug and note.
      const ids = isAgency ? (data || []).filter((r) => r.kind === 'plan_replace' && r.status === 'failed').map((r) => r.job_id) : [];
      if (ids.length) {
        const { data: reps } = await supabase.from('v_portal_replacements').select('*').in('job_id', ids);
        if (!cancelled) setFailedReps(Object.fromEntries((reps || []).map((x) => [x.job_id, x])));
      } else {
        setFailedReps({});
      }
    })();
    return () => { cancelled = true; };
  }, [activeId, isAgency, inboxAll]);

  if (rows === null && !error) return <p className="lede">Loading your inbox…</p>;

  const clientName = (id) => clients?.find((c) => c.client_id === id)?.name || id;
  const isAgencyOnly = (id) => clients?.find((c) => c.client_id === id)?.approval_mode === 'agency_only';
  // Session 19: a dismissed or retried failure leaves the inbox at once.
  const failedHandled = (jobId) => (text) => {
    setRows((rs) => (rs || []).filter((r) => r.job_id !== jobId));
    setDoneNote(text); bumpInbox();
  };
  const alertRows   = isAgency ? (rows || []).filter((r) => r.kind === 'analytics_alert') : [];
  const unreadAlerts = alertRows.filter((r) => !r.is_read).length;
  const actionRows  = (rows || []).filter((r) => r.actionable !== false && r.kind !== 'analytics_alert');
  const waitingRows = isAgency ? (rows || []).filter((r) => r.actionable === false) : [];
  // Hotfix: client rows with actionable = false are status notes (a draft
  // being revised behind the agency review gate). Shown read-only; they never
  // count toward the badge.
  const noteRows = isAgency ? [] : (rows || []).filter((r) => r.actionable === false);

  // Session 14: read state changed for these rows (server already updated).
  const setRead = (jobIds, isRead) => {
    const ids = new Set(jobIds);
    setRows((rs) => (rs || []).map((r) => (ids.has(r.job_id) ? { ...r, is_read: isRead } : r)));
    bumpInbox();
  };
  const markAllShown = async () => {
    setMarkingAll(true); setMarkErr(null);
    const { error: e } = await markAlertsRead(inboxAll ? null : activeId);
    setMarkingAll(false);
    if (e) { setMarkErr(e.message); return; }
    setRead(alertRows.filter((r) => !r.is_read).map((r) => r.job_id), true);
  };

  return (
    <>
      <SectionHeading
        title={isAgency ? 'Needs your action' : 'Needs your attention'}
        explainer="portal_overview" label="the portal" />
      <p className="lede">
        {isAgency
          ? <>Everything waiting on you {inboxAll ? 'across every client' : <>for {activeClient?.name || 'this client'}</>}. Items already with a client sit below, read-only, until the client answers.</>
          : <>Everything waiting on {activeClient?.name || 'you'} in one place. Reply to the matching email, or handle it here. Both go to the same place.</>}
      </p>

      {isAgency && (
        <div className="inbox-scope">
          <span className="hint">Showing {inboxAll ? 'all clients' : (activeClient?.name || 'this client')}</span>
          <button type="button" className="link-btn" onClick={() => setInboxAll(!inboxAll)}>
            {inboxAll ? `Only ${activeClient?.name || 'this client'}` : 'Show all clients'}
          </button>
        </div>
      )}

      {isAgency && !inboxAll && onb && !checklistComplete(onb) && <OnboardingChecklist row={onb} compact />}

      {/* Session 8 Part 2: one headline tile when the client has Results. */}
      {!(isAgency && inboxAll) && <ResultsHomeTile />}

      {error && <div className="empty"><h2>Couldn't load your inbox</h2><p>{error}</p></div>}
      {doneNote && <div className="banner banner-ok">{doneNote}</div>}

      {!error && noteRows.length > 0 && (
        <div className="attention-list attention-list-waiting">
          {noteRows.map((r) => (
            <div key={r.job_id} className="attention-card attention-card-waiting">
              <div className="attention-body">
                <span className="attention-kind">{kindTag(r.kind)}</span>
                <div className="attention-title">{r.title}</div>
                <div className="attention-sub">{r.label}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {!error && rows && actionRows.length === 0 && unreadAlerts === 0 && (
        <div className="empty">
          <h2>You're all caught up</h2>
          <p>{isAgency ? 'Nothing needs your action right now.' : 'New items land here as soon as we need something from you.'}</p>
        </div>
      )}

      {!error && actionRows.length > 0 && (
        <div className="attention-list">
          {actionRows.map((r) => (r.kind === 'plan_replace' && r.status === 'failed' && isAgency) ? (
            <div key={r.job_id} className="attention-card attention-card-static">
              <div className="attention-body">
                <span className="attention-kind">{kindTag(r.kind)}</span>
                <span className="attention-client">{clientName(r.client_id)}</span>
                <div className="attention-title">{r.label}</div>
                <div className="attention-sub">
                  {r.title}{r.publish_date ? ` · scheduled ${formatDate(r.publish_date)}` : ''}
                </div>
                {failedReps[r.job_id]
                  ? <FailedReplace rep={failedReps[r.job_id]} agencyOnly={isAgencyOnly(r.client_id)} onDone={failedHandled(r.job_id)} />
                  : <p className="hint" style={{ margin: '6px 0 0' }}>Loading…</p>}
              </div>
              <Link to={clientPath(r.route, r.client_id)} className="attention-cta">Open plan →</Link>
            </div>
          ) : (
            <Link key={r.job_id} to={clientPath(r.route, r.client_id)} className="attention-card">
              <div className="attention-body">
                <span className="attention-kind">{kindTag(r.kind)}</span>
                {isAgency && <span className="attention-client">{clientName(r.client_id)}</span>}
                <div className="attention-title">{r.label}</div>
                <div className="attention-sub">
                  {r.title}{r.publish_date ? ` · scheduled ${formatDate(r.publish_date)}` : ''}
                </div>
                {r.auto_approves && <DeadlineNote at={r.deadline_at} agency={isAgency} />}
              </div>
              <div className="attention-cta">Open →</div>
            </Link>
          ))}
        </div>
      )}

      {!error && alertRows.length > 0 && (
        <>
          <div className="section-heading">
            <h2>Traffic alerts</h2>
            <span className="alert-heading-actions">
              <span className="count">{unreadAlerts} unread</span>
              {unreadAlerts > 0 && (
                <button type="button" className="link-btn" disabled={markingAll} onClick={markAllShown}>Mark all as read</button>
              )}
            </span>
          </div>
          {markErr && <p className="hint">Couldn't update the alerts: {markErr}</p>}
          {inboxAll
            ? <AlertGroups rows={alertRows} clientName={clientName} onChange={setRead} />
            : <AlertRows rows={alertRows} clientName={clientName} onChange={setRead} />}
        </>
      )}

      {!error && waitingRows.length > 0 && (
        <>
          <div className="section-heading">
            <h2>Waiting on client</h2>
            <span className="count">{waitingRows.length} sent</span>
          </div>
          <div className="attention-list attention-list-waiting">
            {waitingRows.map((r) => (
              <Link key={r.job_id} to={clientPath(r.route, r.client_id)} className="attention-card attention-card-waiting" aria-label={`${clientName(r.client_id)}: ${r.title}, ${r.label}`}>
                <div className="attention-body">
                  <span className="attention-kind">{kindTag(r.kind)}</span>
                  <span className="attention-client">{clientName(r.client_id)}</span>
                  <div className="attention-title">{r.title}</div>
                  <div className="attention-sub">
                    {r.label}{r.sort_at ? ` · sent ${formatDate(r.sort_at)}` : ''}
                  </div>
                  {r.auto_approves && <DeadlineNote at={r.deadline_at} agency />}
                </div>
              </Link>
            ))}
          </div>
        </>
      )}

      {isAgency && !error && <ApprovalLog clientName={clientName} clientId={inboxAll ? null : activeId} />}
    </>
  );
}

// Session 13: recent decisions, with an "auto" badge on approvals made by the
// deadline (lotus_approvals.source = auto_deadline). Session 17: follows the
// Inbox scope like every other row: clientId = the switcher's client, or null
// in "All clients" mode (every client, each row tagged with its name).
function ApprovalLog({ clientName, clientId }) {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => { setRows(null); }, [clientId]);
  useEffect(() => {
    if (!open || rows) return undefined;
    let cancelled = false;
    (async () => {
      let q = supabase.from('v_portal_approval_log').select('*');
      if (clientId) q = q.eq('client_id', clientId);
      const { data } = await q.order('received_at', { ascending: false }).order('id', { ascending: false }).limit(30);
      if (!cancelled) setRows(data || []);
    })();
    return () => { cancelled = true; };
  }, [open, rows, clientId]);
  const label = (r) => (r.decision === 'approved' ? 'Approved'
    : r.decision === 'answered' ? (r.auto ? 'Went ahead without answers' : 'Answered')
    : r.decision === 'flagged' ? 'Changes requested' : r.decision);
  const who = (r) => (r.auto ? 'deadline' : r.stage === 'agency_final' ? 'you (final)'
    : r.stage === 'agency' ? 'you' : r.acted_as === 'dl_owner' ? 'you, as Digital Lotus'   // Session 21
    : r.source === 'email' ? 'client, by email' : 'client');
  return (
    <section className="approval-log">
      <div className="section-heading">
        <h2>Recent approvals</h2>
        <button type="button" className="link-btn" onClick={() => setOpen((o) => !o)}>{open ? 'Hide' : 'Show'}</button>
      </div>
      {open && rows === null && <p className="hint">Loading…</p>}
      {open && rows && rows.length === 0 && <p className="hint">Nothing yet.</p>}
      {open && rows && rows.length > 0 && (
        <div className="card">
          {rows.map((r) => (
            <div key={r.id} className="approval-log-row">
              <span className="attention-kind">{kindTag(r.kind)}</span>
              <span className="attention-client">{r.client_name || clientName(r.client_id)}</span>
              <span className="approval-log-title">{r.title}</span>
              <span className="approval-log-what">{label(r)} by {who(r)}{r.auto && <AutoBadge />}</span>
              <span className="approval-log-when">{fmtDay(r.received_at)}</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
